import React, { useState, useContext, useRef } from 'react';
import { SoundContext } from '../Context/SoundContext';
import prawnImage from '../Assets/prawn.png';
import prawn8kImage from '../Assets/prawn-8k.png';
import eyeIconImage from '../Assets/eye-icon.png';
import friedPrawnImage from '../Assets/fried_prawn.png';
import deadPrawnImage from '../Assets/deadprawn.png';
import speakerIcon from '../Assets/speaker-icon.png';
import bookIcon from '../Assets/book.png';
import wiseprawnImage from '../Assets/wiseprawn.png';
import labsyPhImage from '../Assets/labsy-ph.png';
import { useModal, MODAL_TYPES } from '../Context/ModalContext';
import UnderwaterBloodEffect from '../Components/UnderwaterBloodEffect';
import MovingSprites from '../Components/MovingSprites';
import { useBatchUpdates } from '../Utils/batchUpdates';
import { trackEvent } from '../Utils/analytics';

// Module-level preload — runs at import time so all sprites are in the browser cache
// before the user enters vibe mode. Refs held in array to prevent early GC.
// eslint-disable-next-line no-unused-vars
const _vibeSprites = [
  prawnImage, prawn8kImage, eyeIconImage, friedPrawnImage,
  deadPrawnImage, speakerIcon, bookIcon, wiseprawnImage, labsyPhImage,
].map(src => { const img = new window.Image(); img.src = src; return img; });

// Persist a per-user easter egg ID so it stays stable across sessions
const EASTER_EGG_ID_STORAGE_KEY = 'prawnhub-easter-egg-id';
const getEasterEggId = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  let id = window.localStorage.getItem(EASTER_EGG_ID_STORAGE_KEY);
  if (!id) {
    const rawId = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : Math.random().toString(36);
    // Normalize to a short 8-char ID (uppercase, no dashes)
    id = rawId.replace(/-/g, '').slice(0, 8).toUpperCase();
    window.localStorage.setItem(EASTER_EGG_ID_STORAGE_KEY, id);
  }
  return id;
};

const PRAWN_SIZE_RANGE = [48, 72]; // px (unified with KillSimulator)
const PRAWN_SPEED_RANGE = [1.1, 1.4]; // px/frame (unified with KillSimulator)

const VibeSimulator = ({ goToKill, openPunishmentModal, prawnStates, setPrawnStates, isHdPrawnMode, setIsHdPrawnMode }) => {
  const { playSoundFx } = useContext(SoundContext);
  const { batchUpdate } = useBatchUpdates();


  // Wisdom quote tracking
  const { allQuotesUsed } = require('../Data/wisdomQuotes');

  const { modal, openModal, setLastSimulator } = useModal();
  // Prawn states are now managed by parent component (RootLayout) for persistence
  // Each prawn: { alive, isFried }
  const [bloodEffect, setBloodEffect] = useState(null); // { x, y, onComplete }
  const [laserEffect, setLaserEffect] = useState(null); // { x, y, hit, id }
  const [shouldShowDeadPrawn, setShouldShowDeadPrawn] = useState(false); // Control dead prawn appearance
  const [prawnShotInProgress, setPrawnShotInProgress] = useState(false); // Track if prawn shot is happening
  const [isFirstKill, setIsFirstKill] = useState(true); // Track if this is the first prawn kill
  const [hdPrawnEffect, setHdPrawnEffect] = useState(null); // { x, y, id } for "HD Prawn" text effect

  // Prevent punishment modal from reopening immediately after close
  const punishmentLockRef = useRef(false);
  // Track if a sprite was hit to prevent miss sound
  const spriteHitRef = useRef(false);

  // Unified shooting logic for prawns
  const handlePrawnShot = (i, x, y) => {
    if (modal.isOpen) return;
    
    // Set flag to prevent miss sound
    spriteHitRef.current = true;
    
    setLaserEffect({ x, y, hit: true, id: Date.now() + Math.random() });
    setTimeout(() => setLaserEffect(null), 180);
    setPrawnStates(prev => {
      const newStates = [...prev];
      newStates[i].alive = false;
      return newStates;
    });
    
    // Track that a prawn shot is in progress
    setPrawnShotInProgress(true);
    window.prawnShotInProgress = true; // Global flag for MovingSprites throttling
    
    // Play appropriate sound effect based on whether this is the first kill
    if (isFirstKill) {
      playSoundFx('shrimpShotHeartbroken');
      trackEvent('vibe_prawn_shot', { first_kill: true });
      setIsFirstKill(false); // Mark that first kill has happened
    } else {
      playSoundFx('shrimpShotShort');
      trackEvent('vibe_prawn_shot', { first_kill: false });
    }
    
    setBloodEffect({
      x, y,
      onComplete: () => {
        // Use batchUpdate to prevent CLS timing collisions with MovingSprites animation loop
        batchUpdate(() => {
          setBloodEffect(null);
          // Clear the shot in progress flag
          setPrawnShotInProgress(false);
          window.prawnShotInProgress = false; // Clear global flag
          // Only open punishment modal if not locked
          if (openPunishmentModal && !punishmentLockRef.current) openPunishmentModal();
        });
      }
    });
  };

  // Speaker icon shot handler
  const handleSpeakerShot = (i, x, y) => {
    if (modal.isOpen) return;

    // Set flag to prevent miss sound
    spriteHitRef.current = true;

    setLaserEffect({ x, y, hit: true, id: Date.now() + Math.random() });
    setTimeout(() => setLaserEffect(null), 180);
  // Play bookShot SFX for speaker icon
  playSoundFx('bookShot');
  trackEvent('sprite_shot', { sprite: 'speaker' });
    if (typeof openModal === 'function') {
      openModal(MODAL_TYPES.SOUND_SETTINGS);
    } else if (typeof openPunishmentModal === 'function') {
      openPunishmentModal();
    }
  };

  // Book icon shot handler
  const handleBookShot = (i, x, y) => {
    if (modal.isOpen) return;
    // Set flag to prevent miss sound
    spriteHitRef.current = true;
    setLaserEffect({ x, y, hit: true, id: Date.now() + Math.random() });
    setTimeout(() => setLaserEffect(null), 180);
    // Play book shot SFX and don't stop audio
    playSoundFx('bookShot');
    trackEvent('sprite_shot', { sprite: 'book' });
    if (typeof openModal === 'function') {
      openModal(MODAL_TYPES.WISDOM);
    }
  };

  // Wiseprawn shot handler - plays bookShot and opens WISDOM modal with easter egg quote
  const handleWisePrawnShot = (i, x, y) => {
    if (modal.isOpen) return;
    spriteHitRef.current = true;
    setLaserEffect({ x, y, hit: true, id: Date.now() + Math.random() });
    setTimeout(() => setLaserEffect(null), 180);
    playSoundFx('bookShot');
    setLastSimulator('vibe');
    const eggId = getEasterEggId();
    trackEvent('easter_egg_found', { sprite: 'wiseprawn', egg_id: eggId });
    const easterEggQuote = {
      text: `You have found an easter egg. Email sup@labbylabs.xyz a message with 'egg ID: ${eggId}' to claim your prize`,
      attribution: 'Labby Labs',
    };
    if (typeof openModal === 'function') {
      openModal(MODAL_TYPES.WISDOM, { wisdomQuote: easterEggQuote });
    }
  };

  // Dead prawn shot handler - triggers about modal
  const handleDeadPrawnShot = (i, x, y) => {
    if (modal.isOpen) return;
    
    // Set flag to prevent miss sound
    spriteHitRef.current = true;
    
    setLaserEffect({ x, y, hit: true, id: Date.now() + Math.random() });
    setTimeout(() => setLaserEffect(null), 180);
    // Play plastic shot sound for dead prawn
    playSoundFx('plasticShot1');
    trackEvent('sprite_shot', { sprite: 'dead_prawn' });
    // Set last simulator so the Back button works properly
    setLastSimulator('vibe');
    if (typeof openModal === 'function') {
      openModal(MODAL_TYPES.ABOUT);
    }
  };

  // Eye icon shot handler — toggles HD Prawn Mode
  const handleEyeShot = (i, x, y) => {
    if (modal.isOpen) return;
    spriteHitRef.current = true;
    setLaserEffect({ x, y, hit: true, id: Date.now() + Math.random() });
    setTimeout(() => setLaserEffect(null), 180);
    playSoundFx('bookShot');
    const goingHd = !isHdPrawnMode;
    trackEvent('hd_prawn_toggled', { enabled: goingHd });
    if (typeof setIsHdPrawnMode === 'function') {
      setIsHdPrawnMode(goingHd);
    }
    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    let left, top, transform = '';
    if (x > screenW * 0.75) {
      left = x - 120;
      transform = 'translateY(-100%)';
    } else {
      left = x + 24;
      transform = 'translateY(-100%)';
    }
    if (y < screenH * 0.2) {
      top = y + 32;
      transform = '';
    } else {
      top = y - 24;
    }
    setHdPrawnEffect({ x: left, y: top, transform, isOff: !goingHd, id: Date.now() + Math.random() });
    setTimeout(() => setHdPrawnEffect(null), 1500);
  };

  // Labsy sprite shot handler — opens the WTF (About) modal
  const handleLabsyShot = (i, x, y) => {
    if (modal.isOpen) return;
    spriteHitRef.current = true;
    setLaserEffect({ x, y, hit: true, id: Date.now() + Math.random() });
    setTimeout(() => setLaserEffect(null), 180);
    playSoundFx('plasticShot4');
    trackEvent('sprite_shot', { sprite: 'labsy' });
    setLastSimulator('vibe');
    if (typeof openModal === 'function') {
      openModal(MODAL_TYPES.ABOUT);
    }
  };

  const allPrawnsDead = prawnStates.every(prawn => !prawn.alive);
  const alivePrawnsCount = prawnStates.filter(prawn => prawn.alive).length;

  // Reset first kill flag when prawns are revived (all become alive again)
  React.useEffect(() => {
    const allAlive = prawnStates.every(prawn => prawn.alive);
    if (allAlive && !isFirstKill) {
      setIsFirstKill(true);
    }
  }, [prawnStates, isFirstKill]);

  // Effect to show dead prawn when user returns to vibe simulator after all prawns are killed
  React.useEffect(() => {
    if (allPrawnsDead && !modal.isOpen && !prawnShotInProgress) {
      // Show dead prawn immediately when:
      // 1. All prawns are dead
      // 2. No modal is open (user has returned to vibe)
      // 3. No prawn shot is in progress (blood effect has finished)
      setShouldShowDeadPrawn(true);
    } else if (!allPrawnsDead) {
      // Reset dead prawn visibility if somehow prawns become alive again
      setShouldShowDeadPrawn(false);
    }
  }, [allPrawnsDead, modal.isOpen, prawnShotInProgress]);

  // Lock punishment modal reopening for 250ms after modal closes
  React.useEffect(() => {
    if (!modal.isOpen &&
      (modal.type === MODAL_TYPES.PUNISHMENT || modal.type === MODAL_TYPES.GAME_LOST)) {
      punishmentLockRef.current = true;
      const timeout = setTimeout(() => {
        punishmentLockRef.current = false;
      }, 250);
      return () => clearTimeout(timeout);
    }
  }, [modal.isOpen, modal.type]);

  // Miss click logic (laser effect only)
  React.useEffect(() => {
    if (modal.isOpen) return;
    
    const handleMissClick = (e) => {
      // Only trigger if click is not on a sprite or header/nav element
      const element = document.elementFromPoint(e.clientX, e.clientY);
      if (
        element && (
          element.classList.contains('logo') ||
          element.classList.contains('nav-link-left') ||
          element.classList.contains('nav-link-center') ||
          element.classList.contains('mute-button-header') ||
          element.classList.contains('mute-nav-link') ||
          (
            element.classList.contains('nav-link') &&
            !element.classList.contains('nav-link-left') &&
            !element.classList.contains('nav-link-center')
          )
        )
      ) return;
      
      // Use setTimeout to let sprite click handlers run first
      setTimeout(() => {
        if (spriteHitRef.current) {
          spriteHitRef.current = false; // Reset the flag
          return;
        }
        
        setLaserEffect({ x: e.clientX, y: e.clientY, hit: false, id: Date.now() + Math.random() });
        setTimeout(() => setLaserEffect(null), 400);
        // Play missed shot SFX
        playSoundFx('laserMiss');
      }, 0);
    };
    
    // Use capture phase to ensure this runs before sprite click handlers
    window.addEventListener('mousedown', handleMissClick, true);
    // Touch support for mobile
    const handleMissTouch = (e) => {
      const touch = e.touches[0];
      if (!touch) return;
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      if (
        element && (
          element.classList.contains('logo') ||
          element.classList.contains('nav-link-left') ||
          element.classList.contains('nav-link-center') ||
          element.classList.contains('mute-button-header') ||
          element.classList.contains('mute-nav-link') ||
          (
            element.classList.contains('nav-link') &&
            !element.classList.contains('nav-link-left') &&
            !element.classList.contains('nav-link-center')
          )
        )
      ) return;
      setTimeout(() => {
        if (spriteHitRef.current) {
          spriteHitRef.current = false;
          return;
        }
        setLaserEffect({ x: touch.clientX, y: touch.clientY, hit: false, id: Date.now() + Math.random() });
        setTimeout(() => setLaserEffect(null), 400);
        playSoundFx('laserMiss');
      }, 0);
    };
    window.addEventListener('touchstart', handleMissTouch, true);
    
    // Additional event listeners to prevent text selection on long press/hold
    const preventSelection = (e) => {
      e.preventDefault();
    };
    
    const preventDragStart = (e) => {
      e.preventDefault();
      return false;
    };
    
    // Prevent selection events
    document.addEventListener('selectstart', preventSelection, false);
    document.addEventListener('dragstart', preventDragStart, false);
    window.addEventListener('dragstart', preventDragStart, false);
    
    return () => {
      window.removeEventListener('mousedown', handleMissClick, true);
      window.removeEventListener('touchstart', handleMissTouch, true);
      document.removeEventListener('selectstart', preventSelection, false);
      document.removeEventListener('dragstart', preventDragStart, false);
      window.removeEventListener('dragstart', preventDragStart, false);
    };
  }, [modal.isOpen, playSoundFx]);

  // Render prawns using MovingSprites
  return (
    <>
      {!modal.isOpen && (
        <div className="simulator vibe-simulator">
          <h2>Just Vibe</h2>
          {/* Render alive prawns */}
          {alivePrawnsCount > 0 && (
            <MovingSprites
              count={5}
              images={prawnStates.map(s => s.isFried ? friedPrawnImage : (isHdPrawnMode ? prawn8kImage : prawnImage))}
              sizeRange={PRAWN_SIZE_RANGE}
              speedRange={PRAWN_SPEED_RANGE}
              zIndex={20}
              spriteState={prawnStates}
              setSpriteState={setPrawnStates}
              onShot={handlePrawnShot}
              className={isHdPrawnMode ? 'prawn hd-prawn' : 'prawn'}
              disabled={false}
              facing="left"
            />
          )}
          {/* Render dead prawns when all prawns are dead and user has returned from modal */}
          {shouldShowDeadPrawn && (
            <MovingSprites
              count={1}
              images={[deadPrawnImage]}
              sizeRange={PRAWN_SIZE_RANGE}
              speedRange={PRAWN_SPEED_RANGE}
              zIndex={20}
              spriteState={[{ alive: true }]} // Dead prawn is always "alive" for rendering purposes
              setSpriteState={() => {}} // No-op
              onShot={handleDeadPrawnShot}
              className="dead-prawn"
              disabled={false}
              facing="left"
            />
          )}
          {/* Speaker Icon Sprite */}
          <MovingSprites
            count={1}
            images={[speakerIcon]}
            sizeRange={[29, 31]} // Fixed size for speaker icon
            speedRange={[0.4, 0.5]} // speed for speaker icon
            zIndex={19}
            spriteState={[{ alive: true }]} // Always visible and shootable
            setSpriteState={() => {}} // No-op
            onShot={handleSpeakerShot}
            className="speaker-icon-sprite"
            disabled={false}
            facing="neither"
          />
          {/* Book Icon Sprite or Wiseprawn Sprite */}
          {!allQuotesUsed() ? (
            <MovingSprites
              count={1}
              images={[bookIcon]}
              sizeRange={[36, 36]} // Fixed size for book icon
              speedRange={[0.3, 0.4]} // speed for book icon
              zIndex={18}
              spriteState={[{ alive: true }]} // Always visible and shootable
              setSpriteState={() => {}} // No-op
              onShot={handleBookShot}
              className="book-icon-sprite"
              disabled={false}
              facing="neither"
            />
          ) : (
            <MovingSprites
              count={1}
              images={[wiseprawnImage]}
              sizeRange={PRAWN_SIZE_RANGE}
              speedRange={PRAWN_SPEED_RANGE}
              zIndex={18}
              spriteState={[{ alive: true }]}
              setSpriteState={() => {}}
              onShot={handleWisePrawnShot}
              className="wiseprawn-sprite prawn"
              disabled={false}
              facing="left"
            />
          )}
          {/* Eye Icon Sprite — HD Prawn Mode toggle */}
          <MovingSprites
            count={1}
            images={[eyeIconImage]}
            sizeRange={[36, 36]}
            speedRange={[0.35, 0.45]}
            zIndex={17}
            spriteState={[{ alive: true }]}
            setSpriteState={() => {}}
            onShot={handleEyeShot}
            className={isHdPrawnMode ? 'eye-icon-sprite eye-icon-active' : 'eye-icon-sprite'}
            disabled={false}
            facing="neither"
          />
          {/* Labsy sprite — opens WTF modal when shot */}
          <MovingSprites
            count={1}
            images={[labsyPhImage]}
            sizeRange={[24, 30]}
            speedRange={[0.4, 0.5]}
            zIndex={16}
            spriteState={[{ alive: true }]}
            setSpriteState={() => {}}
            onShot={handleLabsyShot}
            className="labsy-sprite"
            disabled={false}
            facing="right"
          />
        </div>
      )}
      {/* HD Prawn Mode activation text effect */}
      {hdPrawnEffect && (
        <div
          key={hdPrawnEffect.id}
          style={{
            position: 'fixed',
            left: hdPrawnEffect.x,
            top: hdPrawnEffect.y,
            zIndex: 99999,
            color: '#222',
            fontWeight: 700,
            fontSize: '1.1rem',
            textShadow: '0 1px 20px #ffb300, 0 0 14px #ff8c00, 0 0 32px #ff6600',
            pointerEvents: 'none',
            opacity: 1,
            animation: 'hd-prawn-fade 1.5s linear',
            userSelect: 'none',
            transform: hdPrawnEffect.transform,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1.1,
          }}
        >
          <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>HD</span>
          <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Prawn</span>
          {hdPrawnEffect.isOff && <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>OFF</span>}
        </div>
      )}
      {/* Render laser effect for hits */}
      {laserEffect && laserEffect.hit && (
        <div
          className="laser-flash"
          style={{
            position: 'fixed',
            left: laserEffect.x - 1,
            top: laserEffect.y - 1,
            width: 2,
            height: 2,
            pointerEvents: 'none',
            zIndex: 9999,
            borderRadius: '50%',
            boxShadow: '0 0 2px 1px #afb3eb, 0 0 0.5px 0.25px #afb3eb',
            background: 'radial-gradient(circle, #afb3eb 60%, transparent 100%)',
            opacity: 0.85,
            animation: 'laser-flash-hit 0.18s linear',
          }}
        />
      )}
      {/* Render laser effect for misses (simulate shrink/fade) */}
      {laserEffect && laserEffect.hit === false && (
        <div
          className="laser-flash-miss"
          style={{
            position: 'fixed',
            left: laserEffect.x - 1,
            top: laserEffect.y - 1,
            width: 2,
            height: 2,
            pointerEvents: 'none',
            zIndex: 9999,
            borderRadius: '50%',
            boxShadow: '0 0 2px 1px #afb3eb, 0 0 0.5px 0.25px #afb3eb',
            background: 'radial-gradient(circle, #afb3eb 60%, transparent 100%)',
            opacity: 0.85,
            animation: 'laser-flash-miss 0.4s linear',
            willChange: 'opacity, transform',
            contain: 'layout style paint',
            backfaceVisibility: 'hidden',
          }}
        />
      )}
      {/* Render blood effect - Always mount to prevent CLS */}
      <UnderwaterBloodEffect
        x={bloodEffect?.x || 0}
        y={bloodEffect?.y || 0}
        onComplete={bloodEffect?.onComplete || (() => {})}
        isActive={!!bloodEffect}
      />
      {/* Copyright notice */}
      <div 
        className="copyright-notice"
        style={{
          position: 'fixed',
          bottom: '10px',
          left: '6vw',
          transform: 'translateX(-50%)',
          fontSize: '12px',
          fontWeight: '500',
          color: '#fff',
          textShadow: '0px 0px 2px #000, 0px 0px 3px #000',
          zIndex: 30,
          userSelect: 'none',
          opacity: 0.8,
          pointerEvents: 'none',
        }}
      >
        © {new Date().getFullYear()} Labby Labs LLC
      </div>

      <style>{`
        @keyframes laser-flash-hit {
          0% { opacity: 0.85; transform: scale(1); }
          60% { opacity: 1; transform: scale(1.2); }
          100% { opacity: 0; transform: scale(1.4); }
        }
        @keyframes laser-flash-miss {
          0% { opacity: 0.85; transform: scale(1); }
          30% { opacity: 0.85; transform: scale(1); }
          60% { opacity: 0.5; transform: scale(0.7); }
          100% { opacity: 0; transform: scale(0.1); }
        }
        @keyframes hd-prawn-fade {
          0%   { opacity: 1; }
          80%  { opacity: 1; }
          100% { opacity: 0; }
        }
        .hd-prawn {
        }
        .eye-icon-active {
          filter: drop-shadow(0 0 6px rgba(140, 60, 255, 0.9)) drop-shadow(0 0 3px rgba(255, 200, 50, 0.8));
        }
          .simulator {
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            touch-action: manipulation;
            -webkit-touch-callout: none;
            -webkit-tap-highlight-color: transparent;
          }
        @media (max-width: 768px) {
          .copyright-notice {
            left: 20vw !important;
          }
        }
      `}</style>
    </>
  );
};

export default VibeSimulator;
